/**
 * FE-23: Global Filters — Platform / Country / Language dropdowns.
 * Filters are synced to URL query params so metrics tabs can read them.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import { t } from "../i18n";

// ─── Types ───

const DEFAULT_PLATFORM_OPTIONS = [
  "ios",
  "ipados",
  "android",
  "macos",
  "windows",
  "admin",
] as const;

type GeoOption = {
  readonly code: string;
  readonly label: string;
};

type LocaleOption = {
  readonly code: string;
  readonly label: string;
};

type DimensionOptionRow = {
  readonly country?: string | null;
  readonly locale?: string | null;
  readonly platform?: string | null;
};

type OptionSetters = {
  readonly setPlatforms: (v: GeoOption[]) => void;
  readonly setCountries: (v: GeoOption[]) => void;
  readonly setLocales: (v: LocaleOption[]) => void;
  readonly setFailures: (v: ReadonlyArray<string>) => void;
};

export type GlobalFilterValues = {
  readonly platform: string;
  readonly country: string;
  readonly locale: string;
};

// ─── Main Component ───

export function GlobalFilters(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries, error, locales, platforms } = useFilterOptions();

  const platform = searchParams.get("platform") ?? "all";
  const country = searchParams.get("country") ?? "all";
  const locale = searchParams.get("locale") ?? "all";

  const updateParam = useCallback(
    (key: string, value: string): void => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <div>
      <div style={s.row}>
        <FilterSelect
          label={t("filters.platform")}
          value={platform}
          onChange={(v) => updateParam("platform", v)}
          options={[
            { value: "all", label: t("filters.all_platforms") },
            ...platforms.map((p) => ({
              value: p.code,
              label: platformLabel(p.code),
            })),
          ]}
        />
        <FilterSelect
          label={t("filters.country")}
          value={country}
          onChange={(v) => updateParam("country", v)}
          options={[
            { value: "all", label: t("filters.all_countries") },
            ...countries.map((c) => ({ value: c.code, label: c.label })),
          ]}
        />
        <FilterSelect
          label={t("filters.language")}
          value={locale}
          onChange={(v) => updateParam("locale", v)}
          options={[
            { value: "all", label: t("filters.all_languages") },
            ...locales.map((l) => ({ value: l.code, label: l.label })),
          ]}
        />
      </div>
      {error && (
        <p role="alert" style={s.error}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Filter Select ───

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element {
  return (
    <label style={s.label}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={s.select}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Hook: read global filters from URL ───

export function useGlobalFilters(): GlobalFilterValues {
  const [searchParams] = useSearchParams();
  const platform = searchParams.get("platform") ?? "all";
  const country = searchParams.get("country") ?? "all";
  const locale = searchParams.get("locale") ?? "all";
  return useMemo(
    () => ({ platform, country, locale }),
    [platform, country, locale],
  );
}

// ─── Hook: fetch dropdown options ───

function useFilterOptions(): {
  platforms: ReadonlyArray<GeoOption>;
  countries: ReadonlyArray<GeoOption>;
  locales: ReadonlyArray<LocaleOption>;
  error: string | null;
} {
  const [platforms, setPlatforms] = useState<GeoOption[]>(
    DEFAULT_PLATFORM_OPTIONS.map(toOption),
  );
  const [countries, setCountries] = useState<GeoOption[]>([]);
  const [locales, setLocales] = useState<LocaleOption[]>([]);
  const [failures, setFailures] = useState<ReadonlyArray<string>>([]);

  useEffect(() => {
    const controller = { cancelled: false };
    void loadOptions(controller, {
      setPlatforms,
      setCountries,
      setLocales,
      setFailures,
    });
    return () => {
      controller.cancelled = true;
    };
  }, []);

  return {
    platforms,
    countries,
    locales,
    error: failures.length === 0 ? null : failures.join("; "),
  };
}

// Loads each dimension independently. Unlike a fail-fast Promise.all, a single
// dimension failure no longer wipes the other dropdowns or fails silently —
// the failed dimension keeps its prior options and its error is surfaced.
async function loadOptions(
  controller: { cancelled: boolean },
  setters: OptionSetters,
): Promise<void> {
  const query = defaultMetricsRangeQuery();
  const [platformRes, countryRes, localeRes] = await Promise.allSettled([
    fetchPlatformOptions(query),
    fetchCountryOptions(query),
    fetchLocaleOptions(query),
  ]);
  if (controller.cancelled) return;

  const failures: string[] = [];
  if (platformRes.status === "fulfilled") {
    setters.setPlatforms(
      mergeOptions(DEFAULT_PLATFORM_OPTIONS.map(toOption), platformRes.value),
    );
  } else {
    failures.push(optionFailureMessage("filters.platform", platformRes.reason));
  }
  if (countryRes.status === "fulfilled") {
    setters.setCountries(countryRes.value);
  } else {
    failures.push(optionFailureMessage("filters.country", countryRes.reason));
  }
  if (localeRes.status === "fulfilled") {
    setters.setLocales(localeRes.value);
  } else {
    failures.push(optionFailureMessage("filters.language", localeRes.reason));
  }
  setters.setFailures(failures);
}

async function fetchPlatformOptions(query: string): Promise<GeoOption[]> {
  const res = await apiFetch<{
    data: { items: ReadonlyArray<DimensionOptionRow> };
  }>(`/admin/metrics/platform?${query}`);
  return toDimensionOptions(res.data.items, "platform");
}

async function fetchCountryOptions(query: string): Promise<GeoOption[]> {
  const res = await apiFetch<{
    data: { items: ReadonlyArray<DimensionOptionRow> };
  }>(`/admin/metrics/geo?${query}`);
  return toDimensionOptions(res.data.items, "country");
}

async function fetchLocaleOptions(query: string): Promise<GeoOption[]> {
  const res = await apiFetch<{
    data: { items: ReadonlyArray<DimensionOptionRow> };
  }>(`/admin/metrics/locale?${query}`);
  return toDimensionOptions(res.data.items, "locale");
}

function toDimensionOptions(
  rows: ReadonlyArray<DimensionOptionRow>,
  field: "platform" | "country" | "locale",
): GeoOption[] {
  const seen = new Set<string>();
  const options: GeoOption[] = [];
  for (const row of rows) {
    const code = row[field]?.trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    options.push({ code, label: code });
  }
  return options;
}

function mergeOptions(
  defaults: ReadonlyArray<GeoOption>,
  fetched: ReadonlyArray<GeoOption>,
): GeoOption[] {
  const seen = new Set<string>();
  const options: GeoOption[] = [];
  for (const option of [...defaults, ...fetched]) {
    if (seen.has(option.code)) continue;
    seen.add(option.code);
    options.push(option);
  }
  return options;
}

function toOption(code: string): GeoOption {
  return { code, label: capitalize(code) };
}

function defaultMetricsRangeQuery(): string {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 30);
  const query = new URLSearchParams({
    start_date: toDateParam(startDate),
    end_date: toDateParam(endDate),
  });
  return query.toString();
}

function toDateParam(date: Date): string {
  return date.toISOString().slice(0, 10);
}

// ─── Helpers ───

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function optionFailureMessage(labelKey: string, error: unknown): string {
  const detail =
    error instanceof Error ? error.message : t("common.failed_to_load");
  return `${t(labelKey)}: ${detail}`;
}

// Known platforms get a localized label; dynamically-discovered codes
// fall back to a capitalized code (t() returns the key when untranslated).
function platformLabel(code: string): string {
  const key = `filters.platform_${code}`;
  const translated = t(key);
  return translated === key ? capitalize(code) : translated;
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  row: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 20,
    padding: "12px 0",
    borderBottom: "1px solid var(--color-border)",
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  select: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    background: "var(--color-bg)",
    color: "var(--color-text)",
    minWidth: 140,
  },
  error: {
    color: "var(--color-danger)",
    fontSize: 12,
    marginTop: 0,
    marginBottom: 16,
  },
};
